/**
 * NotificationDisplay Component
 *
 * Displays temporary notifications in the corner of the screen.
 * Similar to SpeedIndicator but for warnings/info messages.
 */

import { CSSProperties } from 'react';
import { useNotificationStore } from '@/stores/notificationStore';

function NotificationDisplay() {
  const notifications = useNotificationStore((state) => state.notifications);
  const dismissNotification = useNotificationStore((state) => state.dismissNotification);

  if (notifications.length === 0) {
    return null;
  }

  const containerStyles: CSSProperties = {
    position: 'fixed',
    bottom: '180px', // Above timeline
    right: '20px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    pointerEvents: 'none',
  };

  const getNotificationStyles = (type: string): CSSProperties => {
    let backgroundColor = 'var(--nb-accent-primary)'; // info
    
    if (type === 'warning') {
      backgroundColor = '#ff9800'; // Orange
    } else if (type === 'error') {
      backgroundColor = '#f44336'; // Red
    }

    return {
      backgroundColor,
      color: 'white',
      padding: '12px 16px',
      borderRadius: 'var(--nb-radius-md)',
      fontSize: 'var(--nb-font-size-sm)',
      fontWeight: 500,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '300px',
      animation: 'slideInRight 0.3s ease-out',
      pointerEvents: 'auto',
      cursor: 'pointer',
    };
  };

  return (
    <div style={containerStyles}>
      {notifications.map((notification) => (
        <div
          key={notification.id}
          style={getNotificationStyles(notification.type)}
          onClick={() => dismissNotification(notification.id)}
          title="Click to dismiss"
        >
          {notification.message}
        </div>
      ))}
      <style>{`
        @keyframes slideInRight {
          from { 
            opacity: 0; 
            transform: translateX(100px);
          }
          to { 
            opacity: 1; 
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}

export default NotificationDisplay;
